import React, { useEffect, useState } from "react";
import { animated, useSpring, useTransition } from "@react-spring/web";

import { TabBar } from "src/components/tabbar";
import Button from "src/components/button";
import { toast } from "react-toastify";
import Toast from "src/components/toast";
import AccountSettings from "./accountSettings";
import CollectionSettings from "./collectionSettings";
import ShareSettings from "./shareSettings";
import AssetSettings from "./assetSettings";
import DealRoomSettings from "./dealRoomSettings";

import { useAccountQuery, useAccountUpdate } from "src/query/settings/accountManagement";
import { convertBannerToBase64, validateBannerBackground, validateLogo } from "src/services/accountManagement";

import { updatingAccountSucceeded } from "src/redux/actions/accounts";
import { MediaFile } from "src/types/media";
import { useDispatch } from "react-redux";
import userRoleService from "src/utils/userRoles.service";

import { Account } from "src/types/accounts";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";

interface TabSectionProps {
  selected: number;
  index: number;
  children: React.ReactNode;
}

const AccountManagement: React.FC = () => {

  const [formData, setFormData] = useState<Partial<Account>>({});
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const [resetEditor, setResetEditor] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const dispatch = useDispatch();

  const { data: account } = useAccountQuery();

  const { mutate, isPending } = useAccountUpdate();

  const isUpdating = isBannerUploading || isPending;

  const isAccountModified = !!account && !_.isEmpty(formData) && !_.isEqual(formData, account);

  const actionTransitions = useTransition(isAccountModified, {
    from: { opacity: 0, transform: "translateY(-10px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(-10px)" },
    config: { tension: 220, friction: 20 },
  });

  const handleAccountSettingsUpdate = (field: string, value: any) => {
    setFormData((prev) => {
      return {
        ...prev,
        [field]: value,
      };
    });
  };

  const handleSettingsChange = (key: "shareSettings" | "contentSettings") =>
    (updatedSettings: any) => {
      setFormData(prev => ({
        ...prev,
        [key]: updatedSettings,
      }));
    };

  const tabConfiguration = [
    {
      title: "Account Settings",
      subTitle: "Configure your workspace identity and branding",
      render: (
        <AccountSettings
          data={formData}
          handleAccountSettingsUpdate={handleAccountSettingsUpdate}
        />
      ),
      visible: true,
    },
    {
      title: "Collection Settings",
      subTitle: "Build your brand experience across all shared content",
      render: (
        <CollectionSettings
          data={formData}
          handleAccountSettingsUpdate={handleAccountSettingsUpdate}
          resetEditor={resetEditor}
        />
      ),
      visible: userRoleService.canAccessCollection(),
    },
    {
      title: "Share Settings",
      subTitle: "Control how recipients interact with your content",
      render: (
        <ShareSettings
          data={formData}
          handleAccountSettingsUpdate={handleSettingsChange("shareSettings")}
        />
      ),
      visible: userRoleService.canShare(),
    },
    {
      title: "Asset Settings",
      subTitle: "Manage content lifecycle and security",
      render: (
        <AssetSettings
          data={formData}
          handleAccountSettingsUpdate={handleSettingsChange("contentSettings")}
        />
      ),
      visible: true,
    },
    {
      title: "Deal Room Settings",
      subTitle: "Create premium experiences for key accounts",
      render: (
        <DealRoomSettings
          data={formData}
          handleAccountSettingsUpdate={handleAccountSettingsUpdate}
          resetEditor={resetEditor}
        />
      ),
      visible: userRoleService.canAccessDealRoom(),
    },
  ];

  const permittedTabs = tabConfiguration.filter(tab => tab.visible);

  const handleSave = async () => {
    setIsBannerUploading(true);
    const collectionBannerBackground = formData.collectionSettings?.banner?.background as MediaFile | undefined;
    const dealRoomBannerBackground = formData.dealRoomSettings?.banner?.background as MediaFile | undefined;
    const collectionBannerBase64 = await convertBannerToBase64(collectionBannerBackground);
    const dealRoomBannerBase64 = await convertBannerToBase64(dealRoomBannerBackground);

    const collectionSettingsWithBase64 = {
      ...formData.collectionSettings,
      banner: {
        ...formData.collectionSettings?.banner,
        background: collectionBannerBase64,
        mediaType: collectionBannerBackground?.type,
      },
    };

    const dealRoomSettingsWithBase64 = {
      ...formData.dealRoomSettings,
      banner: {
        ...formData.dealRoomSettings?.banner,
        background: dealRoomBannerBase64,
        mediaType: dealRoomBannerBackground?.type,
      },
    };

    const collectionSettings = validateBannerBackground(collectionSettingsWithBase64);
    const dealRoomSettings = validateBannerBackground(dealRoomSettingsWithBase64);
    const shareSettings = formData.shareSettings || {};
    const logo = validateLogo(formData.logo);

    const account = {
      name: formData.name,
      companyUrl: formData.companyUrl,
      favIcon: null,
      logo,
      collectionSettings,
      dealRoomSettings,
      shareSettings,
      contentSettings: formData.contentSettings || {},
    } as Partial<Account>;

    mutate(account, {
      onSuccess: (response) => {
        dispatch(updatingAccountSucceeded(response));
        setIsBannerUploading(false);
        toast(<Toast.Sucess heading="Success" message="Saved changes" />);
      },
      onError: (error) => {
        console.error("Save failed:", error);
        setIsBannerUploading(false);
      },
    });
  };

  const handleCancel = () => {
    setIsBannerUploading(false);
    if (account) {
      setFormData(_.cloneDeep(account));
      setResetEditor(prev => !prev); 
    }
  };

  useEffect(() => {
    if (account) {
      setFormData(_.cloneDeep(account));
    }
  }, [account]);

  return (
    <div className={styles.container}>
      <div className={styles.actionBar}>
        <div className={styles.header}>
          <div className={styles.title}>Account Management</div>
          <div className={styles.subTitle}>
            {permittedTabs[activeTab]?.subTitle}
          </div>
        </div>
        {account && actionTransitions((style, item) => {
          if (!item) return null;

          return (
            <animated.div style={style} className={styles.actionButtons}>
              <div className={styles.actionsInfo}>click here to</div>
              <Button variant="primary" text={isUpdating ? "Saving..." : "Save"} disabled={isUpdating} icon="paperflite-tick" onClick={handleSave} />
              <Button variant="secondary" text="Cancel" onClick={handleCancel} />
            </animated.div>
          );
        })}
      </div>
      <div className={styles.tabSection}>
        <TabBar active={activeTab} onSelectionChange={setActiveTab} gap={32}>
          {permittedTabs.map((tab, index) => (
            <div
              key={index}
              className={cx(styles.tab, { [styles.active]: activeTab === index })}
            >
              {tab.title}
            </div>
          ))}
        </TabBar>
      </div>

      <div className={styles.tabContentWrapper}>
        {permittedTabs.map((tab, index) => (
          <TabSection key={index} index={index} selected={activeTab}>
            {tab.render}
          </TabSection>
        ))}
      </div>
    </div>
  );
};

export default AccountManagement;

const TabSection: React.FC<TabSectionProps> = ({
  index,
  selected,
  children,
}) => {
  const active = index === selected;
  const [animation] = useSpring(
    {
      height: active ? "auto" : 0,
      opacity: active ? 1 : 0,
      top: active ? 0 : 100,
      config: {
        duration: 200,
        friction: 7,
        tension: 120,
      }
    },
    [active],
  );

  if (!active) {
    return null;
  }

  return (
    <animated.div
      style={animation}
      className={cx(styles.tabBody, styles.stack)}
    >
      {children}
    </animated.div>
  );
};